import React, { useRef, useEffect, useState } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../../utils/constants';

let _googleMapsPromise = null;
export const loadGoogleMaps = () => {
  if (_googleMapsPromise) return _googleMapsPromise;
  _googleMapsPromise = new Promise((resolve) => {
    try {
      if (!GOOGLE_MAPS_API_KEY) { resolve(false); return; }
      if (window.google?.maps?.importLibrary) { resolve(true); return; }
      window.__loftMapsReady = () => resolve(true);
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=__loftMapsReady`;
      script.async = true;
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    } catch (e) {
      console.error('Failed to load Google Maps:', e);
      resolve(false);
    }
  });
  return _googleMapsPromise;
};

// Places API (New). The legacy places.Autocomplete widget needs the legacy
// Places API, which Google no longer enables on new projects (loft-golf has
// only Places API (New)).
let _placesPromise = null;
const loadPlaces = () => {
  if (_placesPromise) return _placesPromise;
  _placesPromise = loadGoogleMaps()
    .then((loaded) => (loaded ? window.google.maps.importLibrary('places') : null))
    .catch((e) => {
      console.error('Failed to load Places library:', e);
      return null;
    });
  return _placesPromise;
};

// Closest match to the legacy 'address' type collection (max 5 types allowed)
const ADDRESS_TYPES = ['street_address', 'premise', 'subpremise', 'route'];
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

// Address Autocomplete Component using Google Places API (New)
// onSelect(locationData) is called with { displayName, lat, lon, zipCode } when a suggestion is picked
const GooglePlacesAutocomplete = ({ value, onChange, onSelect, placeholder, className, required = false }) => {
  const inputRef = useRef(null);
  const placesRef = useRef(null);
  const mountedRef = useRef(true);
  const sessionRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const callbacksRef = useRef({ onChange, onSelect });
  callbacksRef.current = { onChange, onSelect };

  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    mountedRef.current = true;
    loadPlaces().then((places) => {
      if (mountedRef.current) placesRef.current = places;
    });
    const debounce = debounceRef;
    return () => {
      mountedRef.current = false;
      clearTimeout(debounce.current);
    };
  }, []);

  const closeList = () => {
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const fetchSuggestions = async (input) => {
    const places = placesRef.current;
    if (!places) return;
    const requestId = ++requestIdRef.current;
    try {
      // One session token covers the keystrokes and the final pick, so Google
      // bills the whole lookup as one session
      if (!sessionRef.current) sessionRef.current = new places.AutocompleteSessionToken();
      const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionRef.current,
        includedRegionCodes: ['us'],
        includedPrimaryTypes: ADDRESS_TYPES,
      });
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      setSuggestions(results.map((s) => s.placePrediction).filter(Boolean).slice(0, 5));
      setActiveIndex(-1);
    } catch (e) {
      if (mountedRef.current && requestId === requestIdRef.current) closeList();
      console.error('Autocomplete error:', e);
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    callbacksRef.current.onChange(text);
    clearTimeout(debounceRef.current);
    if (text.trim().length < MIN_CHARS) {
      requestIdRef.current++;
      closeList();
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  const selectPrediction = async (prediction) => {
    requestIdRef.current++;
    closeList();
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress', 'location', 'addressComponents'] });
      sessionRef.current = null; // fetchFields ends the billing session
      if (!mountedRef.current || !place.location) return;

      const displayName = place.formattedAddress || prediction.text.toString();
      const lat = place.location.lat();
      const lon = place.location.lng();
      const zipComponent = place.addressComponents?.find(c => c.types.includes('postal_code'));
      const zipCode = zipComponent?.longText || '';

      if (inputRef.current) inputRef.current.value = displayName;
      callbacksRef.current.onChange(displayName);
      if (callbacksRef.current.onSelect) {
        callbacksRef.current.onSelect({ displayName, lat, lon, zipCode });
      }
    } catch (e) {
      console.error('Place details error:', e);
    }
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault(); // pick the suggestion instead of submitting the form
      selectPrediction(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      closeList();
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        defaultValue={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={closeList}
        placeholder={placeholder}
        className={className}
        required={required}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-black border border-gray-700 rounded-lg overflow-hidden shadow-lg">
          {suggestions.map((prediction, i) => (
            <li
              key={prediction.placeId}
              // mousedown + preventDefault so the input's blur doesn't close the list before the pick lands
              onMouseDown={(e) => { e.preventDefault(); selectPrediction(prediction); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`px-3 py-2 text-sm cursor-pointer text-white ${i === activeIndex ? 'bg-gray-800' : ''}`}
            >
              {prediction.text.toString()}
            </li>
          ))}
          {/* Google requires attribution when showing Places results without a map */}
          <li className="px-3 py-1 text-xs text-gray-400 text-right">Powered by Google</li>
        </ul>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
